import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Calendar, Clock, Building2, Package, Check, AlertCircle, Loader2, Send } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { toast } from "sonner";

interface QuotationItem {
  id: string;
  description: string;
  quantity: number;
  measurement_unit: string | null;
  specifications: string | null;
  supplier_code: string | null;
  sort_order: number;
}

interface QuotationResponse {
  id: string;
  item_id: string;
  unit_price: number | null;
  total_price: number | null;
  delivery_days: number | null;
  notes: string | null;
  available: boolean;
  ipi_rate?: number | null;
  icms_rate?: number | null;
  st_rate?: number | null;
}

interface QuotationData {
  supplier: {
    id: string;
    status: string;
    payment_conditions: string | null;
    freight_value: number | null;
    proposal_validity: string | null;
    supplier_notes: string | null;
    response_received_at: string | null;
    supplier_name: string | null;
    supplier_email: string | null;
    additional_products?: string | null;
  };
  quotation: {
    id: string;
    request_number: string;
    title: string;
    description: string | null;
    deadline: string | null;
    status: string;
  };
  company: {
    name: string;
    razao_social: string | null;
    logo_url: string | null;
    telefone: string | null;
    email_corporativo: string | null;
  };
  items: QuotationItem[];
  responses: QuotationResponse[];
}

interface ItemResponse {
  unit_price: string;
  delivery_days: string;
  notes: string;
  available: boolean;
  ipi_rate: string;
  icms_rate: string;
  st_rate: string;
}

export default function SupplierQuotationResponse() {
  const { token } = useParams<{ token: string }>();
  const [data, setData] = useState<QuotationData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [itemResponses, setItemResponses] = useState<Record<string, ItemResponse>>({});
  const [paymentConditions, setPaymentConditions] = useState("");
  const [freightValue, setFreightValue] = useState("");
  const [proposalValidity, setProposalValidity] = useState("");
  const [supplierNotes, setSupplierNotes] = useState("");
  const [additionalProducts, setAdditionalProducts] = useState("");

  useEffect(() => {
    if (token) {
      loadQuotationData();
    }
  }, [token]);

  const loadQuotationData = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const { data: result, error: rpcError } = await supabase
        .rpc('get_public_quotation_by_token', { p_token: token });

      if (rpcError) throw rpcError;

      if (!result) {
        setError("Cotação não encontrada ou link expirado.");
        return;
      }

      const quotationData = result as unknown as QuotationData;
      setData(quotationData);

      // Initialize form with existing data
      const initialResponses: Record<string, ItemResponse> = {};
      quotationData.items.forEach((item) => {
        const existingResponse = quotationData.responses?.find(r => r.item_id === item.id);
        initialResponses[item.id] = {
          unit_price: existingResponse?.unit_price?.toString() || "",
          delivery_days: existingResponse?.delivery_days?.toString() || "",
          notes: existingResponse?.notes || "",
          available: existingResponse?.available ?? true,
          ipi_rate: existingResponse?.ipi_rate?.toString() || "",
          icms_rate: existingResponse?.icms_rate?.toString() || "",
          st_rate: existingResponse?.st_rate?.toString() || "",
        };
      });
      setItemResponses(initialResponses);

      // Initialize footer fields
      setPaymentConditions(quotationData.supplier.payment_conditions || "");
      setFreightValue(quotationData.supplier.freight_value?.toString() || "");
      setProposalValidity(quotationData.supplier.proposal_validity || "");
      setSupplierNotes(quotationData.supplier.supplier_notes || "");
      setAdditionalProducts(quotationData.supplier.additional_products || "");

      // Check if already responded
      if (quotationData.supplier.status === 'responded') {
        setShowThankYou(true);
      }
    } catch (err) {
      console.error("Erro ao carregar cotação:", err);
      setError("Erro ao carregar a cotação. Tente novamente.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleItemChange = (itemId: string, field: keyof ItemResponse, value: string | boolean) => {
    setItemResponses(prev => ({
      ...prev,
      [itemId]: {
        ...prev[itemId],
        [field]: value,
      },
    }));
  };

  const calculateTotal = (itemId: string) => {
    const item = data?.items.find(i => i.id === itemId);
    const response = itemResponses[itemId];
    if (!item || !response?.unit_price) return 0;
    return parseFloat(response.unit_price) * item.quantity;
  };

  const calculateGrandTotal = () => {
    if (!data) return 0;
    let total = data.items.reduce((sum, item) => sum + calculateTotal(item.id), 0);
    if (freightValue) {
      total += parseFloat(freightValue) || 0;
    }
    return total;
  };

  const handleSubmit = async () => {
    if (!data || !token) return;

    // Validate required fields
    const emptyPrices = data.items.filter(item => {
      const response = itemResponses[item.id];
      return !response?.unit_price || parseFloat(response.unit_price) <= 0;
    });

    if (emptyPrices.length > 0) {
      toast.error("Por favor, preencha o preço unitário de todos os itens.");
      return;
    }

    try {
      setIsSubmitting(true);

      const responses = data.items.map(item => ({
        item_id: item.id,
        unit_price: parseFloat(itemResponses[item.id]?.unit_price || "0"),
        delivery_days: parseInt(itemResponses[item.id]?.delivery_days || "0") || null,
        notes: itemResponses[item.id]?.notes || null,
        available: itemResponses[item.id]?.available ?? true,
        ipi_rate: parseFloat(itemResponses[item.id]?.ipi_rate || "0") || 0,
        icms_rate: parseFloat(itemResponses[item.id]?.icms_rate || "0") || 0,
        st_rate: parseFloat(itemResponses[item.id]?.st_rate || "0") || 0,
      }));

      const { data: result, error: saveError } = await supabase
        .rpc('save_public_quotation_response', {
          p_token: token,
          p_responses: responses,
          p_payment_conditions: paymentConditions || null,
          p_freight_value: freightValue ? parseFloat(freightValue) : 0,
          p_proposal_validity: proposalValidity || null,
          p_supplier_notes: supplierNotes || null,
          p_additional_products: additionalProducts || null,
        });

      if (saveError) throw saveError;

      const response = result as { success: boolean; error?: string };
      
      if (!response.success) {
        toast.error(response.error || "Erro ao enviar proposta");
        return;
      }

      setShowThankYou(true);
      toast.success("Proposta enviada com sucesso!");
    } catch (err) {
      console.error("Erro ao salvar resposta:", err);
      toast.error("Erro ao enviar proposta. Tente novamente.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-4">
          <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto" />
          <p className="text-muted-foreground">Carregando cotação...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center space-y-4">
            <AlertCircle className="h-12 w-12 text-destructive mx-auto" />
            <h2 className="text-lg font-semibold">Cotação não encontrada</h2>
            <p className="text-muted-foreground">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (showThankYou) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center space-y-4">
            <div className="h-16 w-16 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mx-auto">
              <Check className="h-8 w-8 text-green-600 dark:text-green-400" />
            </div>
            <h2 className="text-xl font-semibold">Proposta Enviada!</h2>
            <p className="text-muted-foreground">
              Obrigado! Sua cotação foi recebida com sucesso.
            </p>
            <div className="bg-muted/50 p-4 rounded-lg text-sm space-y-1">
              <p><strong>Cotação:</strong> {data?.quotation.request_number}</p>
              <p><strong>Título:</strong> {data?.quotation.title}</p>
              {data?.supplier.response_received_at && (
                <p><strong>Enviado em:</strong> {format(new Date(data.supplier.response_received_at), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}</p>
              )}
            </div>
            <p className="text-sm text-muted-foreground">
              A empresa entrará em contato em caso de aprovação.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!data) return null;

  const isDeadlinePassed = data.quotation.deadline && new Date(data.quotation.deadline) < new Date();

  return (
    <div className="min-h-screen bg-muted/30">
      {/* Header */}
      <header className="bg-background border-b sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            {data.company.logo_url ? (
              <img 
                src={data.company.logo_url} 
                alt={data.company.name} 
                className="h-12 w-auto object-contain"
              />
            ) : (
              <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center">
                <Building2 className="h-6 w-6 text-primary" />
              </div>
            )}
            <div className="flex-1">
              <h1 className="font-semibold text-lg">
                {data.company.razao_social || data.company.name}
              </h1>
              <p className="text-sm text-muted-foreground">
                Solicitação de Cotação {data.quotation.request_number}
              </p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-6 space-y-6">
        {/* Info Card */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">{data.quotation.title}</CardTitle>
            {data.quotation.description && (
              <CardDescription>{data.quotation.description}</CardDescription>
            )}
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-4 text-sm">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <span className="text-muted-foreground">Criado em:</span>
                <span className="font-medium">
                  {format(new Date(), "dd/MM/yyyy", { locale: ptBR })}
                </span>
              </div>
              {data.quotation.deadline && (
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  <span className="text-muted-foreground">Prazo:</span>
                  <Badge variant={isDeadlinePassed ? "destructive" : "outline"}>
                    {format(new Date(data.quotation.deadline), "dd/MM/yyyy", { locale: ptBR })}
                    {isDeadlinePassed && " (Expirado)"}
                  </Badge>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Items Table */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <Package className="h-5 w-5" />
              <CardTitle className="text-lg">Itens da Cotação</CardTitle>
            </div>
            <CardDescription>
              Preencha o preço unitário, prazo de entrega e alíquotas de impostos para cada item
            </CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead className="w-8">#</TableHead>
                    <TableHead>Produto / Descrição</TableHead>
                    <TableHead className="w-20 text-center">Qtd</TableHead>
                    <TableHead className="w-28">Preço Unit.</TableHead>
                    <TableHead className="w-24 text-right">Total</TableHead>
                    <TableHead className="w-20">Prazo</TableHead>
                    <TableHead className="w-20">IPI %</TableHead>
                    <TableHead className="w-20">ICMS %</TableHead>
                    <TableHead className="w-20">ST %</TableHead>
                    <TableHead className="w-32">Obs.</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.items.map((item, index) => (
                    <TableRow key={item.id}>
                      <TableCell className="font-medium text-muted-foreground">
                        {index + 1}
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{item.description}</p>
                          {item.supplier_code && (
                            <p className="text-xs text-muted-foreground">
                              Cód: {item.supplier_code}
                            </p>
                          )}
                          {item.specifications && (
                            <p className="text-xs text-muted-foreground mt-1">
                              {item.specifications}
                            </p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-center font-medium">
                        {item.quantity} {item.measurement_unit || 'UN'}
                      </TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="0,00"
                          value={itemResponses[item.id]?.unit_price || ""}
                          onChange={(e) => handleItemChange(item.id, 'unit_price', e.target.value)}
                          className="h-9"
                          disabled={isDeadlinePassed}
                        />
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {formatCurrency(calculateTotal(item.id))}
                      </TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          min="0"
                          placeholder="dias"
                          value={itemResponses[item.id]?.delivery_days || ""}
                          onChange={(e) => handleItemChange(item.id, 'delivery_days', e.target.value)}
                          className="h-9"
                          disabled={isDeadlinePassed}
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          max="100"
                          placeholder="0"
                          value={itemResponses[item.id]?.ipi_rate || ""}
                          onChange={(e) => handleItemChange(item.id, 'ipi_rate', e.target.value)}
                          className="h-9"
                          disabled={isDeadlinePassed}
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          max="100"
                          placeholder="0"
                          value={itemResponses[item.id]?.icms_rate || ""}
                          onChange={(e) => handleItemChange(item.id, 'icms_rate', e.target.value)}
                          className="h-9"
                          disabled={isDeadlinePassed}
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          max="100"
                          placeholder="0"
                          value={itemResponses[item.id]?.st_rate || ""}
                          onChange={(e) => handleItemChange(item.id, 'st_rate', e.target.value)}
                          className="h-9"
                          disabled={isDeadlinePassed}
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          placeholder="Obs."
                          value={itemResponses[item.id]?.notes || ""}
                          onChange={(e) => handleItemChange(item.id, 'notes', e.target.value)}
                          className="h-9"
                          disabled={isDeadlinePassed}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        {/* Additional Products */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Produtos Adicionais</CardTitle>
            <CardDescription>
              Caso queira sugerir produtos alternativos ou complementares, descreva abaixo
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Textarea
              placeholder="Ex: Produto X pode ser substituído por Produto Y com melhor custo-benefício. Também temos disponível o Produto Z que complementa a solução..."
              value={additionalProducts}
              onChange={(e) => setAdditionalProducts(e.target.value)}
              rows={4}
              disabled={isDeadlinePassed}
            />
          </CardContent>
        </Card>

        {/* Proposal Details */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Dados da Proposta</CardTitle>
            <CardDescription>
              Preencha as condições gerais da sua proposta
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="payment">Condição de Pagamento</Label>
                <Input
                  id="payment"
                  placeholder="Ex: 30/60/90 dias"
                  value={paymentConditions}
                  onChange={(e) => setPaymentConditions(e.target.value)}
                  disabled={isDeadlinePassed}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="freight">Valor do Frete (R$)</Label>
                <Input
                  id="freight"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0,00"
                  value={freightValue}
                  onChange={(e) => setFreightValue(e.target.value)}
                  disabled={isDeadlinePassed}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="validity">Validade da Proposta</Label>
                <Input
                  id="validity"
                  type="date"
                  value={proposalValidity}
                  onChange={(e) => setProposalValidity(e.target.value)}
                  disabled={isDeadlinePassed}
                />
              </div>
              <div className="space-y-2">
                <Label>Total Geral</Label>
                <div className="h-10 px-3 py-2 rounded-md border bg-muted/50 font-semibold">
                  {formatCurrency(calculateGrandTotal())}
                </div>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="notes">Observações Gerais</Label>
              <Textarea
                id="notes"
                placeholder="Informações adicionais sobre sua proposta..."
                value={supplierNotes}
                onChange={(e) => setSupplierNotes(e.target.value)}
                rows={3}
                disabled={isDeadlinePassed}
              />
            </div>
          </CardContent>
        </Card>

        {/* Submit Button */}
        <div className="flex justify-center pb-8">
          <Button
            size="lg"
            onClick={handleSubmit}
            disabled={isSubmitting || isDeadlinePassed}
            className="min-w-64 gap-2"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" />
                Enviando...
              </>
            ) : (
              <>
                <Send className="h-5 w-5" />
                ENVIAR PROPOSTA
              </>
            )}
          </Button>
        </div>

        {isDeadlinePassed && (
          <div className="text-center pb-8">
            <Badge variant="destructive" className="text-sm">
              O prazo para envio desta cotação expirou
            </Badge>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t bg-background py-4 text-center text-sm text-muted-foreground">
        <p>
          {data.company.razao_social || data.company.name}
          {data.company.telefone && ` • ${data.company.telefone}`}
          {data.company.email_corporativo && ` • ${data.company.email_corporativo}`}
        </p>
      </footer>
    </div>
  );
}